import {useEffect, useRef, useState} from "react";

type LogoAnalysisResult = {
    /** Ob das Logo einen Hintergrund-Container braucht */
    needsBackground: boolean;
    /** Ob die Analyse abgeschlossen ist */
    analyzed: boolean;
};

/** Schwellenwert: Ab diesem Anteil transparenter Pixel gilt das Bild als transparent */
const TRANSPARENCY_THRESHOLD = 0.05;

/** Helligkeit unter diesem Wert im Dark Mode erfordert einen weissen Hintergrund */
const DARK_MODE_LIGHTNESS_THRESHOLD = 0.4;

/** Helligkeit ueber diesem Wert im Light Mode erfordert einen grauen Hintergrund */
const LIGHT_MODE_LIGHTNESS_THRESHOLD = 0.85;

/**
 * Berechnet die relative Helligkeit eines RGB-Pixels (0..1).
 * Verwendet die wahrgenommene Helligkeitsformel (Rec. 709 Luma).
 */
function calculateLuminance(r: number, g: number, b: number): number {
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/**
 * Analysiert ein Base64-kodiertes PNG-Logo per Canvas-API und entscheidet,
 * ob es einen Hintergrund-Container benoetigt, damit es im aktuellen Theme
 * gut sichtbar ist.
 *
 * Logik:
 * 1. Keine Transparenz vorhanden -> needsBackground = false (eigener Hintergrund schuetzt)
 * 2. Transparenz vorhanden + Dark Mode + dunkle Pixel -> needsBackground = true
 * 3. Transparenz vorhanden + Light Mode + helle Pixel -> needsBackground = true
 */
function analyzeLogoPixels(
    base64Logo: string,
    theme: "dark" | "light",
): Promise<boolean> {
    return new Promise((resolve) => {
        const img = new Image();

        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    // Fallback: Bei fehlendem Canvas-Context Container anzeigen
                    resolve(true);
                    return;
                }

                ctx.drawImage(img, 0, 0);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const pixels = imageData.data;
                const totalPixels = pixels.length / 4;

                let transparentPixelCount = 0;
                let opaqueLuminanceSum = 0;
                let opaquePixelCount = 0;

                for (let i = 0; i < pixels.length; i += 4) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];
                    const a = pixels[i + 3];

                    if (a < 255) {
                        transparentPixelCount++;
                    }

                    // Nur Pixel mit signifikanter Opazitaet fuer die Helligkeitsanalyse nutzen
                    if (a > 128) {
                        opaqueLuminanceSum += calculateLuminance(r, g, b);
                        opaquePixelCount++;
                    }
                }

                const transparencyRatio = transparentPixelCount / totalPixels;

                // Keine signifikante Transparenz -> Logo hat eigenen Hintergrund
                if (transparencyRatio < TRANSPARENCY_THRESHOLD) {
                    resolve(false);
                    return;
                }

                // Keine auswertbaren opaken Pixel -> sicherheitshalber Container anzeigen
                if (opaquePixelCount === 0) {
                    resolve(true);
                    return;
                }

                const averageLuminance = opaqueLuminanceSum / opaquePixelCount;

                if (theme === "dark") {
                    // Dark Mode: Dunkle Logos brauchen weissen Hintergrund
                    resolve(averageLuminance < DARK_MODE_LIGHTNESS_THRESHOLD);
                } else {
                    // Light Mode: Helle Logos brauchen grauen Hintergrund
                    resolve(averageLuminance > LIGHT_MODE_LIGHTNESS_THRESHOLD);
                }
            } catch {
                // Bei jedem Fehler sicherheitshalber Container anzeigen
                resolve(true);
            }
        };

        img.onerror = () => {
            resolve(true);
        };

        img.src = "data:image/png;base64," + base64Logo;
    });
}

/**
 * Hook zur Analyse eines Base64-kodierten Logos.
 * Entscheidet anhand der Pixel-Daten und des aktuellen Themes,
 * ob das Logo einen Hintergrund-Container benoetigt.
 *
 * @param base64Logo - Base64-String des Logos (ohne data:-Prefix), oder undefined/null
 * @param theme - Aktuelles aufgeloestes Theme ("dark" oder "light")
 * @returns `{ needsBackground, analyzed }` - Ergebnis der Analyse
 */
export function useLogoAnalysis(
    base64Logo: string | undefined | null,
    theme: "dark" | "light",
): LogoAnalysisResult {
    const [result, setResult] = useState<LogoAnalysisResult>({
        needsBackground: true,
        analyzed: false,
    });

    // Cache-Key, um bei gleichem Logo+Theme nicht erneut zu analysieren
    const cacheKeyRef = useRef<string>("");

    useEffect(() => {
        if (!base64Logo) {
            setResult({needsBackground: false, analyzed: true});
            return;
        }

        // Erzeuge einen stabilen Cache-Key aus Logo-Laenge und Theme,
        // damit bei Theme-Wechsel neu analysiert wird
        const newCacheKey = base64Logo.length + ":" + theme;

        if (cacheKeyRef.current === newCacheKey) {
            return;
        }

        cacheKeyRef.current = newCacheKey;
        setResult({needsBackground: true, analyzed: false});

        let cancelled = false;

        analyzeLogoPixels(base64Logo, theme).then((needsBg) => {
            if (!cancelled) {
                setResult({needsBackground: needsBg, analyzed: true});
            }
        });

        return () => {
            cancelled = true;
        };
    }, [base64Logo, theme]);

    return result;
}
